import React from 'react';
import { Belt } from './types';

interface BeltStripesProps {
  belts: Belt[];
  baseBeltSpacing: number;
  stripeSpacings: number[];
  onStripeClick?: (belt: Belt) => void;
}

export const BeltStripes: React.FC<BeltStripesProps> = ({ 
  belts, 
  baseBeltSpacing, 
  stripeSpacings, 
  onStripeClick 
}) => {
  const getBeltPosition = (index: number): number => {
    const startX = 40;
    
    if (index === 0) return startX;
    
    let position = startX;
    
    for (let i = 0; i < index; i++) {
      for (let j = 0; j < 5; j++) {
        const spacingIndex = i * 5 + j;
        position += stripeSpacings[spacingIndex] || baseBeltSpacing / 5;
      }
    }
    
    return position;
  };
  
  const getStripePosition = (beltIndex: number, stripeIndex: number): number => {
    const beltPosition = getBeltPosition(beltIndex);
    
    let stripePosition = beltPosition;
    
    for (let i = 0; i < stripeIndex + 1; i++) {
      const spacingIndex = beltIndex * 5 + i;
      stripePosition += stripeSpacings[spacingIndex] || baseBeltSpacing / 5;
    }
    
    return stripePosition;
  };

  const renderStripesBetweenBelts = () => {
    const allStripes: JSX.Element[] = [];
    
    for (let beltIndex = 0; beltIndex < belts.length; beltIndex++) {
      const currentBelt = belts[beltIndex];
      
      if (beltIndex >= belts.length - 1) continue;
      
      for (let stripeIndex = 0; stripeIndex < 4; stripeIndex++) {
        const stripePosition = getStripePosition(beltIndex, stripeIndex);

        const stripeBelt: Belt = {
          ...currentBelt,
          stripes: stripeIndex + 1,
          isStripe: true,
          name: `${currentBelt.name} belt ${stripeIndex + 1} stripe${stripeIndex === 0 ? '' : 's'}`,
        };

        allStripes.push(
          <React.Fragment key={`stripe-${beltIndex}-${stripeIndex}`}>
            <div
              className="absolute z-10 bg-card"
              style={{
                left: stripePosition,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: '16px',
                height: '16px',
                borderRadius: 0,
              }}
            />
            <div
              className="absolute transform -translate-x-1/2 z-20 cursor-pointer hover:scale-110 hover:brightness-95 transition-all opacity-60 hover:opacity-100 transition-opacity"
              style={{
                left: stripePosition,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                height: '56px',
                width: '16px',
                backgroundColor: 'white',
                border: '2px solid black',
                borderRadius: '4px'
              }}
              onClick={() => onStripeClick && onStripeClick(stripeBelt)}
              role="button"
              tabIndex={0}
              aria-label={stripeBelt.name}
            />
          </React.Fragment>
        );
      }
    }

    return allStripes;
  };

  const renderStripesAfterBlackBelt = () => {
    const blackBeltIndex = belts.findIndex(belt => belt.dbColor === 'black');
    if (blackBeltIndex === -1) return null;
    
    const allStripes: JSX.Element[] = [];
    
    for (let i = 0; i < 4; i++) {
      const stripePosition = getStripePosition(blackBeltIndex, i);
      
      const stripeBelt: Belt = {
        name: `Black belt ${i + 1} stripe${i === 0 ? '' : 's'}`,
        color: '#000000',
        dbColor: 'black',
        stripes: i + 1,
        isStripe: true
      };

      allStripes.push(
  <React.Fragment key={`blackbelt-stripe-${i}`}>
    {/* Track blocker behind stripe */}
    <div
      className="absolute z-10 bg-card"
      style={{
        left: stripePosition,
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: '16px',
        height: '16px',
        borderRadius: 0,
      }}
    />
    
    {/* Stripe marker */}
    <div 
      className="absolute transform -translate-x-1/2 z-20 cursor-pointer hover:scale-110 hover:brightness-95 transition-all opacity-60 hover:opacity-100 transition-opacity"
      style={{ 
        left: stripePosition, 
        top: '50%',
        transform: 'translate(-50%, -50%)',
        height: '56px',
        width: '16px',
        backgroundColor: 'white',
        border: '2px solid black',
        borderRadius: '4px'
      }}
      onClick={() => onStripeClick && onStripeClick(stripeBelt)}
      role="button"
      tabIndex={0}
      aria-label={`Black belt ${i + 1} stripe${i === 0 ? '' : 's'}`}
    />
  </React.Fragment>
);
    }

    return allStripes;
  };

  return (
    <>
      {renderStripesBetweenBelts()}
      {renderStripesAfterBlackBelt()}
    </>
  );
};
